/*
 * Resume picker — "Which design would you like to edit?"
 *
 * The star, moon and location customisers each offer to resume the LAST draft
 * (one localStorage pointer). A customer with two or three designs of the same
 * product in the basket could only ever get back to the last one from the
 * product page. This lists every design of that product family in the live cart
 * (one row per _OrderID: copies of one design share it) plus the last draft if
 * it is not in the cart, and lets the customer pick one.
 *
 * The customisers call it from their resume flows and fall back to their own
 * single-design modal when this asset is missing or there are fewer than two
 * designs. Styled here (light) and in customizer-theme.css (dark).
 */
(function () {
  'use strict';

  var FAMILY_NOUN = { star: 'star map', moon: 'moon map', location: 'location map' };

  // Which customiser a cart line belongs to. product_type separates the three
  // lines; the family moon shares "Personalised Moon Map" with the single moon
  // and has its own customiser, so it is split off by handle.
  function familyOf(item) {
    var type = String(item.product_type || '');
    var handle = String(item.handle || '');
    if (type === 'Personalised Star Map') return 'star';
    if (type === 'Personalised Location Map') return 'location';
    if (type === 'Personalised Moon Map') return /family/.test(handle) ? 'hmoon' : 'moon';
    return null;
  }

  function firstLine(s) {
    return String(s || '').split('\n').map(function (l) { return l.trim(); }).filter(Boolean)[0] || '';
  }

  // Row text for a cart line: the customer's own words first, then where/when.
  function describe(family, p) {
    var primary = '', parts = [];
    var msg = firstLine(p.Message);
    if (/^Handwritten/.test(msg)) msg = 'Handwritten message';
    if (family === 'location') {
      primary = firstLine(p.Title) || firstLine(p.Location) || 'Location map';
      if (p.Title && p.Location) parts.push(firstLine(p.Location));
      if (p.Date) parts.push(p.Date);
    } else {
      primary = msg || firstLine(p.Title) || firstLine(p.Caption) || (family === 'moon' ? 'Moon map' : 'Star map');
      if (p.Location) parts.push(firstLine(p.Location));
      // "13th April 2026 - 11:11 PM": the day is enough to tell designs apart.
      if (p['Date & time']) parts.push(String(p['Date & time']).split(' - ')[0]);
    }
    var size = [p._selected_size, p._selected_type].filter(Boolean).join(' ');
    return { primary: primary, secondary: parts.join(' · '), meta: size };
  }

  function handleOfUrl(u) {
    var m = /\/products\/([^/?#]+)/.exec(String(u || ''));
    return m ? m[1] : '';
  }

  // Designs of `family` in the live cart, one per _OrderID, in cart order.
  function cartDesigns(family) {
    return fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js?t=' + Date.now())
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var seen = {}, out = [];
        (cart.items || []).forEach(function (it) {
          var p = it.properties || {};
          var oid = p._OrderID || p.OrderID;
          if (!oid || seen[oid] || familyOf(it) !== family) return;
          seen[oid] = true;
          var d = describe(family, p);
          var editUrl = (it.url || ('/products/' + it.handle)).split('?')[0] + '?order_id=' + encodeURIComponent(oid);
          out.push({
            orderId: oid,
            inCart: true,
            handle: it.handle || handleOfUrl(it.url),
            editUrl: editUrl,
            thumbs: [p._thumbnail_print, p._thumbnail_final, it.image].filter(Boolean),
            primary: d.primary,
            secondary: d.secondary,
            meta: d.meta
          });
        });
        return out;
      })
      .catch(function () { return []; });
  }

  // True when this row must open on its own product page (e.g. a 3D landmark
  // map while the customer is on the flat location map): the page the cart's
  // own Edit button would open. Same page = hydrate in place.
  function needsNavigation(item) {
    if (!item.inCart || !item.handle) return false;
    return handleOfUrl(window.location.pathname) !== item.handle;
  }

  var CSS =
    '#srp-modal{position:fixed;inset:0;z-index:2147483646;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(20,18,16,0.55);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px);animation:srpFade 200ms ease-out}' +
    '#srp-modal .srp-card{background:#f5efe6;color:#1c1a17;border-radius:14px;padding:24px 20px 18px;width:min(420px,100%);max-height:min(640px,calc(100vh - 32px));display:flex;flex-direction:column;box-shadow:0 18px 60px rgba(0,0,0,0.35);animation:srpPop 220ms cubic-bezier(.2,.9,.3,1.1)}' +
    '#srp-modal .srp-title{margin:0 0 6px;font-size:18px;font-weight:600;letter-spacing:.01em;text-align:center;color:inherit}' +
    '#srp-modal .srp-body{margin:0 0 14px;font-size:13px;line-height:1.5;color:#6b6357;text-align:center}' +
    '#srp-modal .srp-list{list-style:none;margin:0 -4px 14px;padding:4px;overflow-y:auto;display:flex;flex-direction:column;gap:8px;-webkit-overflow-scrolling:touch}' +
    '#srp-modal .srp-item{display:flex;align-items:center;gap:12px;width:100%;padding:8px 12px 8px 8px;border:1px solid #d6cdbf;border-radius:10px;background:#fff;color:inherit;font:inherit;text-align:left;cursor:pointer;transition:background 120ms,border-color 120ms}' +
    '#srp-modal .srp-item:hover{background:#ece4d6;border-color:#c39c71}' +
    '#srp-modal .srp-item:focus-visible{outline:2px solid #c39c71;outline-offset:2px}' +
    '#srp-modal .srp-thumb{flex:0 0 auto;width:48px;height:60px;border-radius:4px;background:#e9e2d6;object-fit:contain;display:block}' +
    '#srp-modal .srp-thumb--none{display:flex;align-items:center;justify-content:center;color:#a39a8c;font-size:20px}' +
    '#srp-modal .srp-text{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:2px}' +
    '#srp-modal .srp-primary{font-size:14px;font-weight:600;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
    '#srp-modal .srp-secondary,#srp-modal .srp-meta{font-size:12px;line-height:1.35;color:#6b6357;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
    '#srp-modal .srp-badge{align-self:flex-start;margin-top:2px;padding:1px 7px;border-radius:999px;font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;background:#efe4d3;color:#8a6a45}' +
    '#srp-modal .srp-badge--draft{background:#e8e8e8;color:#666}' +
    '#srp-modal .srp-chev{flex:0 0 auto;color:#a39a8c}' +
    '#srp-modal .srp-new{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;padding:13px 14px;border-radius:10px;border:1px solid #1c1a17;background:#1c1a17;color:#f5efe6;font:inherit;font-size:14px;font-weight:600;letter-spacing:.02em;cursor:pointer;transition:background 120ms,border-color 120ms}' +
    '#srp-modal .srp-new::before{content:"+";font-size:18px;line-height:1;font-weight:400}' +
    '#srp-modal .srp-new:hover{background:#3a342c;border-color:#3a342c}' +
    '#srp-modal .srp-new:focus-visible{outline:2px solid #c39c71;outline-offset:2px}' +
    '@keyframes srpFade{from{opacity:0}to{opacity:1}}' +
    '@keyframes srpPop{from{transform:scale(.95);opacity:0}to{transform:scale(1);opacity:1}}' +
    '@media (prefers-reduced-motion:reduce){#srp-modal,#srp-modal .srp-card{animation:none}}';

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  // opts: { family, items, onPick(item), onNew(), onDismiss() }
  function show(opts) {
    var items = opts.items || [];
    var noun = FAMILY_NOUN[opts.family] || 'design';
    var inCart = items.filter(function (i) { return i.inCart; }).length;
    var old = document.getElementById('srp-modal');
    if (old) old.remove();

    var overlay = el('div');
    overlay.id = 'srp-modal';
    var style = el('style');
    style.textContent = CSS;
    overlay.appendChild(style);

    var card = el('div', 'srp-card');
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-labelledby', 'srp-title');
    var title = el('h3', 'srp-title', 'Which design would you like to edit?');
    title.id = 'srp-title';
    card.appendChild(title);
    card.appendChild(el('p', 'srp-body', inCart
      ? 'You have ' + inCart + ' ' + noun + (inCart === 1 ? '' : 's') + ' in your cart' +
        (items.length > inCart ? ' and a saved design.' : '.') + ' Pick one to open, or start a new design.'
      : 'Pick a saved design to open, or start a new one.'));

    var list = el('ul', 'srp-list');
    var close = function () { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); document.removeEventListener('keydown', onEsc, true); };
    items.forEach(function (item) {
      var li = el('li');
      var btn = el('button', 'srp-item');
      btn.type = 'button';
      var thumbs = (item.thumbs || []).slice();
      var img;
      if (thumbs.length) {
        img = el('img', 'srp-thumb');
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.src = thumbs.shift();
        img.onerror = function () {
          if (thumbs.length) { img.src = thumbs.shift(); return; }
          img.onerror = null;
          var ph = el('span', 'srp-thumb srp-thumb--none', '✦');
          if (img.parentNode) img.parentNode.replaceChild(ph, img);
        };
      } else {
        img = el('span', 'srp-thumb srp-thumb--none', '✦');
      }
      btn.appendChild(img);
      var text = el('span', 'srp-text');
      text.appendChild(el('span', 'srp-primary', item.primary || 'Your design'));
      if (item.secondary) text.appendChild(el('span', 'srp-secondary', item.secondary));
      if (item.meta) text.appendChild(el('span', 'srp-meta', item.meta));
      text.appendChild(el('span', 'srp-badge' + (item.inCart ? '' : ' srp-badge--draft'), item.inCart ? 'In your cart' : 'Saved draft'));
      btn.appendChild(text);
      var chev = el('span', 'srp-chev');
      chev.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>';
      btn.appendChild(chev);
      btn.addEventListener('click', function () {
        close();
        if (needsNavigation(item)) { window.location.href = item.editUrl; return; }
        try { opts.onPick && opts.onPick(item); } catch (e) { console.warn('[resume-picker] pick failed', e); }
      });
      li.appendChild(btn);
      list.appendChild(li);
    });
    card.appendChild(list);

    var newBtn = el('button', 'srp-new', 'Start a new design');
    newBtn.type = 'button';
    newBtn.addEventListener('click', function () { close(); try { opts.onNew && opts.onNew(); } catch (e) {} });
    card.appendChild(newBtn);
    overlay.appendChild(card);

    // Backdrop / Escape = dismiss, not "Start a new design": the customiser
    // keeps its draft pointer so a refresh offers the list again.
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) { close(); try { opts.onDismiss && opts.onDismiss(); } catch (e2) {} }
    });
    function onEsc(e) {
      if (e.key === 'Escape' && overlay.parentNode) { close(); try { opts.onDismiss && opts.onDismiss(); } catch (e2) {} }
    }
    document.addEventListener('keydown', onEsc, true);
    document.body.appendChild(overlay);
    var first = card.querySelector('.srp-item');
    if (first) try { first.focus({ preventScroll: true }); } catch (e) {}
  }

  // One call for the customisers: the cart's designs of this family plus the
  // (already validated, unpaid) draft when it is not one of them. Resolves to
  // the list; the caller shows the picker when it has two or more entries.
  function gather(family, draft) {
    return cartDesigns(family).then(function (items) {
      if (draft && draft.orderId && !items.some(function (i) { return i.orderId === draft.orderId; })) {
        items.push({
          orderId: draft.orderId,
          inCart: false,
          thumbs: [],
          primary: draft.primary || 'Your last design',
          secondary: draft.secondary || 'Saved, not in your cart yet',
          meta: draft.meta || ''
        });
      }
      return items;
    });
  }

  window.SkylitResumePicker = { cartDesigns: cartDesigns, gather: gather, show: show, familyOf: familyOf };
})();
